import { t } from '@trimble/i18n';
import { ISelectOption } from '@trimble-oss/moduswebcomponents';
import type { LengthUnit } from '@fulcrum/lib';
import {
  Component,
  createEffect,
  createMemo,
  onMount,
  Show,
  untrack,
} from 'solid-js';
import { PaletteStopViewer } from './edit-palette-panel/components/PaletteStopViewer';
import { GradientPreview } from './gradient-preview/GradientPreview';
import { HeatmapSectionLabel } from './components/HeatmapSectionLabel';
import { useVolumeHeatmap } from './VolumeHeatmapProvider';
import type { EditablePalette } from './types/heatmap.types';
import { heatmapPaletteAnalyticsProperties } from './heatmap-palette-analytics';

export interface VolumeHeatmapStyleProps {
  /** Project length unit shown next to boundary-value inputs. Defaults to 'm'. */
  readonly lengthUnit?: LengthUnit;
  readonly canEdit?: boolean;
  onEditPalette?(): void;
  onAddPalette?(): void;
  volumePaletteId?: string;
  onPaletteChange(paletteId: string): void;
}

export const VolumeHeatmapStyle: Component<VolumeHeatmapStyleProps> = (
  props
) => {
  const volumeHeatmap = useVolumeHeatmap();

  onMount(() => {
    if (!props.volumePaletteId) {
      volumeHeatmap.setUseDefaultPalette(true);
    }
  });

  // When a brand-new palette is saved, the provider points selectedPalette at
  // it but nothing has updated the (measurement/pending) volumePaletteId that
  // drives the reconciliation effect below. Promote the new palette to the
  // active selection so it sticks in the dropdown instead of being reverted.
  createEffect(() => {
    const createdId = volumeHeatmap.lastCreatedPaletteId();
    if (!createdId) {
      return;
    }
    if (untrack(() => props.volumePaletteId) !== createdId) {
      props.onPaletteChange(createdId);
    }
    volumeHeatmap.clearLastCreatedPaletteId();
  });

  createEffect(() => {
    const id = props.volumePaletteId;
    if (!id) {
      const fallback = volumeHeatmap.defaultPalette();
      if (
        fallback &&
        untrack(() => volumeHeatmap.selectedPaletteId()) !== fallback.id
      ) {
        volumeHeatmap.setSelectedPaletteId(fallback.id);
      }
      return;
    }

    const match = volumeHeatmap
      .availablePalettes()
      .find((palette) => palette.id === id);
    if (match) {
      if (untrack(() => volumeHeatmap.selectedPaletteId()) !== id) {
        volumeHeatmap.setSelectedPaletteId(id);
      }
      return;
    }

    // Catalogue may lag behind provider selection (e.g. right after create).
    if (untrack(() => volumeHeatmap.selectedPaletteId()) === id) {
      return;
    }

    // Stale id (e.g. deleted palette still stored on the measurement).
    const fallback = volumeHeatmap.defaultPalette();
    if (!fallback) {
      return;
    }
    if (untrack(() => volumeHeatmap.selectedPaletteId()) !== fallback.id) {
      volumeHeatmap.setSelectedPaletteId(fallback.id);
    }
    if (
      props.canEdit !== false &&
      untrack(() => props.volumePaletteId) !== fallback.id
    ) {
      props.onPaletteChange(fallback.id);
    }
  });

  const defaultSuffix = (p: EditablePalette): string =>
    p.default ? t('measurements.volumetric.gradient.defaultSuffix') : '';

  const paletteOptions = createMemo<ISelectOption[]>(() => {
    return volumeHeatmap.availablePalettes().map((palette) => ({
      label: `${t(palette.name)}${defaultSuffix(palette)}`,
      value: palette.id,
    }));
  });

  const selectedValue = createMemo(() => {
    const palette = volumeHeatmap.selectedPalette();
    return palette?.id ?? volumeHeatmap.defaultPalette()?.id ?? '';
  });

  function handlePaletteChange(e: Event): void {
    const id = (e.target as HTMLSelectElement).value;
    const palette = volumeHeatmap.availablePalettes().find((p) => p.id === id);
    if (!palette) {
      return;
    }
    volumeHeatmap.reportPaletteAnalytics({
      action: 'changed',
      ...heatmapPaletteAnalyticsProperties(
        palette,
        volumeHeatmap.availablePalettes().length
      ),
    });
    props.onPaletteChange(palette.id);
  }

  return (
    <div class="flex flex-col gap-4" data-testid="volume-heatmap-editor">
      <div class="flex flex-col gap-1">
        <HeatmapSectionLabel
          uppercase
          label={t('measurements.volumetric.gradient.palette')}
        />
        <div class="flex items-center gap-1">
          <modus-wc-select
            class="flex-1"
            data-testid="gradient-palette-select"
            options={paletteOptions()}
            value={selectedValue()}
            size="md"
            disabled={props.canEdit === false || undefined}
            on:inputChange={handlePaletteChange}
          />
          <modus-wc-button
            // @ts-expect-error -- modus types do not support bool:disabled syntax
            bool:disabled={props.canEdit === false}
            aria-label={t('measurements.volumetric.gradient.editPalette')}
            data-testid="gradient-palette-edit"
            type="button"
            color="tertiary"
            shape="square"
            size="md"
            onClick={() => {
              volumeHeatmap.setEditMode('edit');
              props.onEditPalette?.();
            }}
          >
            <modus-wc-icon name="pencil" size="sm" />
          </modus-wc-button>
          <modus-wc-button
            // @ts-expect-error -- modus types do not support bool:disabled syntax
            bool:disabled={props.canEdit === false}
            aria-label={t('measurements.volumetric.gradient.addPalette')}
            data-testid="gradient-palette-add"
            type="button"
            color="tertiary"
            shape="square"
            size="md"
            onClick={() => {
              volumeHeatmap.beginAddPalette();
              props.onAddPalette?.();
            }}
          >
            <modus-wc-icon name="add" size="sm" />
          </modus-wc-button>
        </div>
      </div>

      <Show when={volumeHeatmap.selectedPalette()}>
        {(palette) => (
          <div class="flex flex-col">
            <PaletteStopViewer
              palette={palette()}
              lengthUnit={props.lengthUnit ?? 'm'}
              editable={false}
            />
            <div
              class="py-lg"
              data-testid="volume-heatmap-colors-texture-divider"
            >
              <div class="border-t border-t-gray-0" role="separator" />
            </div>
            <GradientPreview palette={palette()} />
          </div>
        )}
      </Show>
    </div>
  );
};
